/* eslint-disable max-lines */
import { DatePreset, DateRange, Selectable } from '@hakimo-ui/shared/ui-base';
import {
  CameraFilterValue,
  EmployeeFilterValue,
  LocationFilterValue,
  SourceEntityNameFilterValue,
  TenantFilterValue,
} from '../common';
import { EscalationStatus } from '../escalations';
import { LocationAlarmStatus } from '../location-alarms';
import { AlarmStatus } from './alarms';

export type StatusFilterValue<
  T extends AlarmStatus | LocationAlarmStatus | EscalationStatus
> = {
  values: T[];
  negative: boolean;
};

export type AlarmTypeFilterValue = { values: Selectable[]; negative: boolean };

export type PriorityFilterValue = { values: string[]; negative: boolean };

export type DateTimeFilterValue = DateRange;

export interface TimeFilterValue {
  period: TimePeriod;
  customRangeStart: number;
  customRangeEnd: number;
}

export type SourceSystemFilterValue = Selectable[];

export type AgedAlarmFilterValue = {
  id: string;
  name: string;
  customTime?: string;
};

export type FilterType =
  | 'status'
  | 'time'
  | 'agedAlarm'
  | 'sourceSystem'
  | 'location'
  | 'type'
  | 'sourceEntity'
  | 'employee'
  | 'tenant'
  | 'camera'
  | 'mannedLocation'
  | 'priority'
  | 'escalated'
  | 'tap'
  | 'dateTime';

interface BaseFilterConfig {
  type: FilterType;
  label?: string;
}

export interface DateTimeFilterConfig extends BaseFilterConfig {
  type: 'dateTime';
  defaultValue: DateTimeFilterValue;
  presets?: DatePreset[];
}

export interface StatusFilterOption<
  T extends AlarmStatus | LocationAlarmStatus | EscalationStatus
> {
  id: string;
  name: T;
}

interface StatusFilterConfig<T extends AlarmStatus | LocationAlarmStatus>
  extends BaseFilterConfig {
  type: 'status';
  options: StatusFilterOption<T>[];
  defaultValue: StatusFilterValue<T>;
}

interface TimeFilterConfig extends BaseFilterConfig {
  type: 'time';
  defaultValue: TimeFilterValue;
}

interface AgedAlarmFilterConfig extends BaseFilterConfig {
  type: 'agedAlarm';
  defaultValue: AgedAlarmFilterValue;
}

interface SourceSystemFilterConfig extends BaseFilterConfig {
  type: 'sourceSystem';
  defaultValue: SourceSystemFilterValue;
}

interface LocationFilterConfig extends BaseFilterConfig {
  type: 'location';
  defaultValue: LocationFilterValue;
}

interface TypeFilterConfig extends BaseFilterConfig {
  type: 'type';
  defaultValue: AlarmTypeFilterValue;
}

interface SourceEntityFilterConfig extends BaseFilterConfig {
  type: 'sourceEntity';
  defaultValue: SourceEntityNameFilterValue;
}

interface EmployeeFilterConfig extends BaseFilterConfig {
  type: 'employee';
  defaultValue: EmployeeFilterValue;
}

interface TenantFilterConfig extends BaseFilterConfig {
  type: 'tenant';
  defaultValue: TenantFilterValue;
}
interface MannedLocationFilterConfig extends BaseFilterConfig {
  type: 'mannedLocation';
  defaultValue: string | undefined;
}
interface CameraFilterConfig extends BaseFilterConfig {
  type: 'camera';
  defaultValue: CameraFilterValue;
}
interface PriorityFilterConfig extends BaseFilterConfig {
  type: 'priority';
  defaultValue: PriorityFilterValue;
  options: { id: string; name: string }[];
}
interface EscalatedFilterConfig extends BaseFilterConfig {
  type: 'escalated';
  defaultValue: boolean | undefined;
}
interface TapFilterConfig extends BaseFilterConfig {
  type: 'tap';
  defaultValue: number | undefined;
}

export type FilterConfig =
  | StatusFilterConfig<AlarmStatus | LocationAlarmStatus | EscalationStatus>
  | TimeFilterConfig
  | AgedAlarmFilterConfig
  | SourceSystemFilterConfig
  | LocationFilterConfig
  | TypeFilterConfig
  | SourceEntityFilterConfig
  | EmployeeFilterConfig
  | TenantFilterConfig
  | CameraFilterConfig
  | MannedLocationFilterConfig
  | PriorityFilterConfig
  | EscalatedFilterConfig
  | TapFilterConfig
  | DateTimeFilterConfig;

export interface FilterValues {
  status:
    | StatusFilterValue<AlarmStatus>
    | StatusFilterValue<LocationAlarmStatus>;
  time: TimeFilterValue;
  sourceSystem: SourceSystemFilterValue;
  location: LocationFilterValue;
  type: AlarmTypeFilterValue;
  sourceEntity: SourceEntityNameFilterValue;
  employee: EmployeeFilterValue;
  tenant: TenantFilterValue;
  camera: CameraFilterValue; //FilterValues added for Camera
  agedAlarm: AgedAlarmFilterValue;
  mannedLocation: string | undefined;
  priority: PriorityFilterValue;
  escalated: boolean | undefined;
  tap: number | undefined;
  dateTime?: DateTimeFilterValue;
}

export interface Option<T extends string = string> {
  id: string;
  name: T;
}

export enum TimePeriod {
  ALL_TIME = 'All Time',
  PAST_MINUTE = 'Past Minute',
  PAST_HALF_HOUR = 'Past Half Hour',
  PAST_HOUR = 'Past Hour',
  PAST_SIX_HOURS = 'Past 6 Hours',
  PAST_12_HOURS = 'Past 12 Hours',
  PAST_24_HOURS = 'Past 24 Hours',
  PAST_WEEK = 'Past Week',
  PAST_MONTH = 'Past Month',
  PAST_THREE_MONTHS = 'Past 3 Months',
  CUSTOM_RANGE = 'Custom Range',
  CUSTOM_TIME = 'Custom Time',
}

export interface MonitoringFilterValues extends Omit<FilterValues, 'time'> {
  status: StatusFilterValue<AlarmStatus>;
}
